import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../api';
import { fetchDefaultGrouping } from '../utils/storeSettings';

type PromoRow = {
  sku?: string;
  article?: string;
  groupName?: string;
  adsOrders: number;
  adsClicks: number;
  sales: number;
  adsOrdersPrev?: number | null;
  adsClicksPrev?: number | null;
  salesPrev?: number | null;
};

type PromoViewMode = 'products' | 'groups';
type PromoDisplayMode = 'cards' | 'table';

type SortMode =
  | 'drr_desc'
  | 'drr_asc'
  | 'ads_desc'
  | 'ads_asc'
  | 'sales_desc'
  | 'sales_asc';

const fmtNum = (value: number) => {
  const safeValue = value ?? 0;
  const hasFraction = Math.abs(safeValue % 1) > 0;
  const fractionDigits = hasFraction ? 2 : 0;
  return safeValue
    .toLocaleString('ru-RU', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })
    .replace(/\s/g, '\u00A0');
};

const drrTextClass = (drr: number | null) => {
  if (drr == null) return 'text-slate-400';
  if (drr <= 7) return 'text-emerald-600';
  if (drr <= 12) return 'text-amber-600';
  return 'text-rose-600';
};

const deltaMeta = (value: number | null, betterWhen: 'up' | 'down') => {
  if (value == null || !Number.isFinite(value)) return { cls: 'text-slate-400', arrow: '', text: null };
  if (value === 0) return { cls: 'text-slate-400', arrow: '', text: 0 };
  const isUp = value > 0;
  const arrow = isUp ? '▲' : '▼';
  const isGood = betterWhen === 'up' ? isUp : !isUp;
  const cls = isGood ? 'text-emerald-600' : 'text-rose-600';
  return { cls, arrow, text: value };
};

const fmtDeltaK = (value: number) => {
  const abs = Math.abs(value);
  if (!abs) return '';
  return `${(abs / 1000).toFixed(2)}k`;
};

const formatDatePart = (value: number): string => String(value).padStart(2, '0');

const getCurrentMonthValue = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${formatDatePart(now.getMonth() + 1)}`;
};

const getMonthBoundaries = (monthValue: string) => {
  const [yearStr, monthStr] = monthValue.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const start = `${year}-${formatDatePart(month)}-01`;
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const end = `${nextYear}-${formatDatePart(nextMonth)}-01`;
  return { start, end };
};

const getMonthOptions = (count = 12) => {
  const now = new Date();
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    const value = `${date.getFullYear()}-${formatDatePart(date.getMonth() + 1)}`;
    const label = date.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
    return { value, label };
  });
};

const formatDrr = (value: number | null) => {
  if (value == null) return '—';
  return `${value.toFixed(2)} %`;
};

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDateValue = (value: string): Date | null => {
  if (!value) return null;
  const [yearStr, monthStr, dayStr] = value.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return new Date(year, month - 1, day);
};

const PromoByOrdersBlock: React.FC = () => {
  const [rows, setRows] = useState<PromoRow[]>([]);
  const [sortMode, setSortMode] = useState<SortMode>('sales_desc');
  const [groupMode, setGroupMode] = useState<PromoViewMode>('groups');
  const [displayMode, setDisplayMode] = useState<PromoDisplayMode>('cards');
  const [isCardsCollapsed, setIsCardsCollapsed] = useState(false);
  const collapseStorageKey = 'promo_ads_cards_collapsed';
  const userChangedGroup = useRef(false);
  const requestIdRef = useRef(0);
  const initialMonth = getCurrentMonthValue();
  const initialBoundaries = getMonthBoundaries(initialMonth);
  const [dateFrom, setDateFrom] = useState(initialBoundaries.start);
  const [dateTo, setDateTo] = useState(initialBoundaries.end);
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [isRangeModalOpen, setIsRangeModalOpen] = useState(false);
  const [rangeStart, setRangeStart] = useState(initialBoundaries.start);
  const [rangeEnd, setRangeEnd] = useState(initialBoundaries.end);

  const handleViewModeChange = (mode: PromoViewMode) => {
    userChangedGroup.current = true;
    setGroupMode(mode);
  };

  useEffect(() => {
    let isActive = true;
    fetchDefaultGrouping().then(mode => {
      if (!isActive || !mode || userChangedGroup.current) return;
      setGroupMode(mode);
    });
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(collapseStorageKey);
    if (stored === '1') setIsCardsCollapsed(true);
    if (stored === '0') setIsCardsCollapsed(false);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(collapseStorageKey, isCardsCollapsed ? '1' : '0');
  }, [isCardsCollapsed]);

  const load = async (range?: { from: string; to: string; viewMode?: PromoViewMode }) => {
    const requestId = ++requestIdRef.current;
    const from = range?.from ?? dateFrom;
    const to = range?.to ?? dateTo;
    const view = range?.viewMode ?? groupMode;
    const searchParams = new URLSearchParams();
    if (from && to) {
      searchParams.set('dateFrom', from);
      searchParams.set('dateTo', to);
    }
    searchParams.set('view', view);
    const query = searchParams.toString();
    const res = await apiFetch(`/api/dashboard/promo-by-orders${query ? `?${query}` : ''}`);
    if (!res.ok) return;
    const json = await res.json();
    if (requestId !== requestIdRef.current) return;
    setRows(json || []);
  };

  useEffect(() => {
    setRows([]);
    load({ from: dateFrom, to: dateTo, viewMode: groupMode }).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, groupMode]);

  const monthOptions = useMemo(() => getMonthOptions(), []);

  const openRangeModal = () => {
    setRangeStart(dateFrom);
    setRangeEnd(dateTo);
    setIsRangeModalOpen(true);
  };

  const handleMonthChange = (value: string) => {
    const { start, end } = getMonthBoundaries(value);
    setSelectedMonth(value);
    setDateFrom(start);
    setDateTo(end);
  };

  const applyRange = () => {
    setDateFrom(rangeStart);
    setDateTo(rangeEnd);
    setIsRangeModalOpen(false);
  };

  const fillRangeFromMonth = (value: string) => {
    const { start, end } = getMonthBoundaries(value);
    setRangeStart(start);
    setRangeEnd(end);
    setSelectedMonth(value);
  };

  const monthBoundaries = useMemo(
    () => getMonthBoundaries(selectedMonth),
    [selectedMonth],
  );

  const forecastMeta = useMemo(() => {
    const start = parseDateValue(dateFrom);
    const end = parseDateValue(dateTo);
    if (!start || !end || end <= start) {
      return {
        factor: 1,
        elapsedDays: 0,
        totalDays: 0,
        isProjected: false,
        label: 'Прогноз недоступен для выбранного диапазона.',
      };
    }

    const totalDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / DAY_MS));
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowStart = new Date(todayStart.getTime() + DAY_MS);
    const elapsedEnd = new Date(Math.min(end.getTime(), tomorrowStart.getTime()));
    const elapsedDays = Math.max(0, Math.min(totalDays, Math.round((elapsedEnd.getTime() - start.getTime()) / DAY_MS)));
    const factor = elapsedDays > 0 && elapsedDays < totalDays ? totalDays / elapsedDays : 1;
    const isProjected = factor > 1;

    return {
      factor,
      elapsedDays,
      totalDays,
      isProjected,
      label: isProjected
        ? `Прогноз до конца периода по текущему темпу: ${elapsedDays} из ${totalDays} дн.`
        : 'Период завершен или еще не начался: прогноз равен факту.',
    };
  }, [dateFrom, dateTo]);

  const isCustomRange = useMemo(
    () => dateFrom !== monthBoundaries.start || dateTo !== monthBoundaries.end,
    [dateFrom, dateTo, monthBoundaries.end, monthBoundaries.start],
  );

  const items = useMemo(() => {
    const mapped = rows.map(item => {
      const adsTotal = (item.adsClicks || 0) + (item.adsOrders || 0);
      const adsTotalPrev =
        item.adsClicksPrev == null && item.adsOrdersPrev == null
          ? null
          : (item.adsClicksPrev || 0) + (item.adsOrdersPrev || 0);
      const sales = item.sales || 0;
      const drr = sales > 0 ? (adsTotal / sales) * 100 : null;
      const salesPrev = item.salesPrev ?? null;
      const drrPrev =
        adsTotalPrev != null && salesPrev && salesPrev > 0 ? (adsTotalPrev / salesPrev) * 100 : null;
      const displayName =
        groupMode === 'groups' ? item.groupName || '—' : item.article || item.sku || '—';
      return {
        ...item,
        adsTotal,
        adsTotalPrev,
        drr,
        drrPrev,
        salesPrev,
        displayName,
        forecastAdsTotal: adsTotal * forecastMeta.factor,
        forecastOrders: (item.adsOrders || 0) * forecastMeta.factor,
        forecastClicks: (item.adsClicks || 0) * forecastMeta.factor,
        forecastSales: sales * forecastMeta.factor,
        forecastDrr: sales > 0 ? (adsTotal / sales) * 100 : null,
      };
    });

    const sorters: Record<SortMode, (a: typeof mapped[number], b: typeof mapped[number]) => number> = {
      drr_desc: (a, b) => (b.drr ?? -1) - (a.drr ?? -1),
      drr_asc: (a, b) => (a.drr ?? 1e9) - (b.drr ?? 1e9),
      ads_desc: (a, b) => b.adsTotal - a.adsTotal,
      ads_asc: (a, b) => a.adsTotal - b.adsTotal,
      sales_desc: (a, b) => b.sales - a.sales,
      sales_asc: (a, b) => a.sales - b.sales,
    };

    return [...mapped].sort(sorters[sortMode]);
  }, [rows, sortMode, groupMode, forecastMeta.factor]);

  const totals = useMemo(() => {
    if (!items.length) return null;
    return items.reduce(
      (acc, item) => {
        acc.ads += item.adsTotal;
        acc.orders += item.adsOrders || 0;
        acc.clicks += item.adsClicks || 0;
        acc.sales += item.sales || 0;
        acc.forecastAds += item.forecastAdsTotal;
        acc.forecastOrders += item.forecastOrders;
        acc.forecastClicks += item.forecastClicks;
        acc.forecastSales += item.forecastSales;
        return acc;
      },
      { ads: 0, orders: 0, clicks: 0, sales: 0, forecastAds: 0, forecastOrders: 0, forecastClicks: 0, forecastSales: 0 },
    );
  }, [items]);

  return (
    <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow p-4 mb-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <h2 className="text-2xl font-bold text-gray-800">
            Реклама по {groupMode === 'groups' ? 'категориям' : 'товару'}
          </h2>
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
              <button
                type="button"
                className={`px-3 py-1 text-sm font-medium rounded-lg transition ${
                  groupMode === 'products' ? 'bg-indigo-600 text-white shadow' : 'text-slate-600 hover:bg-slate-100'
                }`}
                onClick={() => handleViewModeChange('products')}
              >
                по товарам
              </button>
              <button
                type="button"
                className={`px-3 py-1 text-sm font-medium rounded-lg transition ${
                  groupMode === 'groups' ? 'bg-indigo-600 text-white shadow' : 'text-slate-600 hover:bg-slate-100'
                }`}
                onClick={() => handleViewModeChange('groups')}
              >
                по категориям
              </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-end">
          <label className="text-sm text-gray-600" htmlFor="promo-by-orders-month">
            Месяц:
          </label>
          <select
            id="promo-by-orders-month"
            className="border rounded-lg px-3 py-2 text-sm shadow-sm"
            value={selectedMonth}
            onChange={event => handleMonthChange(event.target.value)}
          >
            {monthOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={openRangeModal}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm border bg-white shadow-sm transition ${
              isCustomRange
                ? 'border-indigo-500 text-indigo-600'
                : 'border-slate-200 text-slate-700 hover:border-indigo-200 hover:text-indigo-600'
            } whitespace-nowrap`}
            title="Выбрать даты"
          >
            <span role="img" aria-label="calendar">📅</span>
            {isCustomRange && <span className="text-xs font-medium">Диапазон</span>}
          </button>
          <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1 shadow-inner">
            <button
              type="button"
              onClick={() => setDisplayMode('cards')}
              className={`p-2 rounded-full transition ${
                displayMode === 'cards'
                  ? 'bg-indigo-600 text-white shadow'
                  : 'text-slate-700 hover:bg-white'
              }`}
              aria-label="Показать карточки"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                className="w-5 h-5"
                aria-hidden="true"
              >
                <rect x="4" y="4" width="7" height="7" rx="1.5" />
                <rect x="13" y="4" width="7" height="7" rx="1.5" />
                <rect x="4" y="13" width="7" height="7" rx="1.5" />
                <rect x="13" y="13" width="7" height="7" rx="1.5" />
              </svg>
              <span className="sr-only">Карточки</span>
            </button>
            <button
              type="button"
              onClick={() => setDisplayMode('table')}
              className={`p-2 rounded-full transition ${
                displayMode === 'table'
                  ? 'bg-indigo-600 text-white shadow'
                  : 'text-slate-700 hover:bg-white'
              }`}
              aria-label="Показать таблицу"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                className="w-5 h-5"
                aria-hidden="true"
              >
                <rect x="3.5" y="5" width="17" height="14" rx="2" />
                <line x1="3.5" y1="10" x2="20.5" y2="10" />
                <line x1="9" y1="5" x2="9" y2="19" />
                <line x1="15" y1="5" x2="15" y2="19" />
              </svg>
              <span className="sr-only">Таблица</span>
            </button>
          </div>
          <label className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm">
            <span className="text-sm text-slate-500">Сортировка</span>
            <select
              className="text-sm font-medium outline-none bg-transparent"
              value={sortMode}
              onChange={event => setSortMode(event.target.value as SortMode)}
            >
              <option value="drr_desc">по ДРР ↓</option>
              <option value="drr_asc">по ДРР ↑</option>
              <option value="ads_desc">по рекламе ↓</option>
              <option value="ads_asc">по рекламе ↑</option>
              <option value="sales_desc">по продажам ↓</option>
              <option value="sales_asc">по продажам ↑</option>
            </select>
          </label>
        </div>
      </div>

      <div className="mb-4 rounded-2xl border border-indigo-100 bg-indigo-50/70 px-4 py-3 text-sm text-slate-700">
        <span className="font-semibold text-slate-900">Прогноз:</span> {forecastMeta.label}
      </div>

      {items.length ? (
        displayMode === 'cards' ? (
          <>
            {totals && (
              <button
                type="button"
                onClick={() => setIsCardsCollapsed(prev => !prev)}
                className="w-full text-left bg-indigo-50 border border-indigo-100 rounded-2xl p-4 shadow-sm hover:shadow-md transition mb-4"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-indigo-600 font-semibold">
                      Итого по всем {groupMode === 'groups' ? 'категориям' : 'товарам'}
                    </div>
                    <div className="mt-1 text-xl font-bold text-slate-900">
                      Реклама: <span className="text-indigo-600">{fmtNum(totals.ads)} ₽</span>
                    </div>
                    <div className="text-sm text-slate-600">
                      DRR:{' '}
                      <span className="font-semibold">
                        {totals.sales ? formatDrr((totals.ads / totals.sales) * 100) : '—'}
                      </span>
                      , Продажи: <span className="font-semibold">{fmtNum(totals.sales)}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-xs md:text-sm">
                    <div className="bg-white rounded-xl px-3 py-2 shadow-sm">
                      <div className="text-slate-500">Заказы</div>
                      <div className="font-semibold text-slate-900">{fmtNum(totals.orders)}</div>
                    </div>
                    <div className="bg-white rounded-xl px-3 py-2 shadow-sm">
                      <div className="text-slate-500">Клики</div>
                      <div className="font-semibold text-slate-900">{fmtNum(totals.clicks)}</div>
                    </div>
                    <div className="bg-white rounded-xl px-3 py-2 shadow-sm">
                      <div className="text-slate-500">Продажи</div>
                      <div className="font-semibold text-indigo-600">{fmtNum(totals.sales)}</div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 rounded-2xl border border-indigo-100 bg-white/80 p-4">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs uppercase tracking-wide text-indigo-600 font-semibold">
                      Прогноз до конца периода
                    </div>
                    <div className="text-xs text-slate-500">
                      {forecastMeta.elapsedDays > 0 && forecastMeta.totalDays > 0
                        ? `${forecastMeta.elapsedDays}/${forecastMeta.totalDays} дн.`
                        : 'нет данных'}
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs md:grid-cols-4 md:text-sm">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-slate-500">Реклама</div>
                      <div className="font-semibold text-slate-900">{fmtNum(totals.forecastAds)} ₽</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-slate-500">Заказы</div>
                      <div className="font-semibold text-slate-900">{fmtNum(totals.forecastOrders)}</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-slate-500">Клики</div>
                      <div className="font-semibold text-slate-900">{fmtNum(totals.forecastClicks)}</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-slate-500">Продажи</div>
                      <div className="font-semibold text-indigo-600">{fmtNum(totals.forecastSales)}</div>
                    </div>
                  </div>
                </div>
              </button>
            )}
            {!isCardsCollapsed && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {items.map(item => {
              const drrText = formatDrr(item.drr);
              const adsDelta = deltaMeta(
                item.adsTotalPrev == null ? null : item.adsTotal - item.adsTotalPrev,
                'down'
              );
              const drrDelta = deltaMeta(
                item.drr == null || item.drrPrev == null ? null : item.drr - item.drrPrev,
                'down'
              );
              const salesDelta = deltaMeta(
                item.salesPrev == null ? null : item.sales - item.salesPrev,
                'up'
              );

              return (
                <article
                  key={groupMode === 'groups' ? item.displayName : `${item.sku}-${item.article}`}
                  className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-slate-300"
                >
                  <div className="mb-3 min-w-0">
                    <div className="font-semibold leading-snug truncate" title={item.displayName}>
                      {item.displayName}
                    </div>
                  </div>

                  <section data-ad-block className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="text-xl sm:text-2xl font-semibold tabular-nums whitespace-nowrap">
                          {fmtNum(item.adsTotal)}
                        </div>
                        <div className="mt-1 flex items-baseline gap-2">
                          <div className="text-[11px] text-slate-500">Реклама</div>
                          <div className={`text-xs font-semibold ${adsDelta.cls}`}>
                            {adsDelta.arrow ? `${adsDelta.arrow} ${fmtDeltaK(adsDelta.text ?? 0)}` : ''}
                          </div>
                        </div>
                      </div>

                      <div className="sm:text-right">
                        <div className="flex items-baseline gap-2 sm:justify-end">
                          <div
                            className={`text-3xl font-semibold leading-none ${drrTextClass(
                              item.drr
                            )} whitespace-nowrap`}
                          >
                            {drrText}
                          </div>
                        </div>
                        <div className="mt-1 flex items-baseline gap-2 sm:justify-end">
                          <div className="text-[11px] text-slate-500">ДРР</div>
                          <div className={`text-xs font-semibold ${drrDelta.cls}`}>
                            {drrDelta.arrow ? `${drrDelta.arrow} ${Math.abs(drrDelta.text ?? 0).toFixed(2)}` : ''}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 px-0.5">
                      <div className="grid grid-cols-2 gap-3 w-full">
                        <div className="rounded-2xl bg-white/70 border border-slate-200 p-2 min-w-0">
                          <div className="text-[11px] text-slate-500">Заказы</div>
                          <div className="mt-1 text-base sm:text-lg font-semibold tabular-nums whitespace-nowrap text-right">
                            {fmtNum(item.adsOrders || 0)}
                          </div>
                        </div>
                        <div className="rounded-2xl bg-white/70 border border-slate-200 p-2 min-w-0">
                          <div className="text-[11px] text-slate-500">Клики</div>
                          <div className="mt-1 text-base sm:text-lg font-semibold tabular-nums whitespace-nowrap text-right">
                            {fmtNum(item.adsClicks || 0)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 rounded-2xl border border-indigo-100 bg-indigo-50/70 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
                        Прогноз до конца периода
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div className="rounded-xl bg-white/80 border border-slate-200 p-2">
                          <div className="text-[11px] text-slate-500">Реклама</div>
                          <div className="mt-1 text-sm font-semibold text-slate-900 text-right">
                            {fmtNum(item.forecastAdsTotal)}
                          </div>
                        </div>
                        <div className="rounded-xl bg-white/80 border border-slate-200 p-2">
                          <div className="text-[11px] text-slate-500">Заказы</div>
                          <div className="mt-1 text-sm font-semibold text-slate-900 text-right">
                            {fmtNum(item.forecastOrders)}
                          </div>
                        </div>
                        <div className="rounded-xl bg-white/80 border border-slate-200 p-2">
                          <div className="text-[11px] text-slate-500">Клики</div>
                          <div className="mt-1 text-sm font-semibold text-slate-900 text-right">
                            {fmtNum(item.forecastClicks)}
                          </div>
                        </div>
                        <div className="rounded-xl bg-white/80 border border-slate-200 p-2">
                          <div className="text-[11px] text-slate-500">Продажи</div>
                          <div className="mt-1 text-sm font-semibold text-indigo-600 text-right">
                            {fmtNum(item.forecastSales)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-dashed border-slate-200 flex items-baseline justify-between gap-3">
                      <div className="flex items-baseline gap-2 min-w-0">
                        <span className="text-slate-500 text-sm">Продажи</span>
                        <span className={`text-xs font-semibold ${salesDelta.cls}`}>
                          {salesDelta.arrow ? `${salesDelta.arrow} ${fmtDeltaK(salesDelta.text ?? 0)}` : ''}
                        </span>
                      </div>
                      <span className="font-semibold text-indigo-600 text-2xl tabular-nums whitespace-nowrap">
                        {fmtNum(item.sales || 0)}
                      </span>
                    </div>
                  </section>
                </article>
              );
                })}
              </div>
            )}
          </>
        ) : (
          <div className="overflow-auto max-h-[70vh] rounded-xl border border-slate-200">
            <table className="min-w-full text-xs sm:text-sm bg-white">
              <thead className="bg-slate-50 text-slate-600 sticky top-0 z-20">
                <tr>
                  <th className="py-2 px-2 text-center">№</th>
                  <th className="py-2 pr-4 text-left">
                    {groupMode === 'groups' ? 'Категория' : 'Товар'}
                  </th>
                  <th className="py-2 pr-4 text-right">Реклама</th>
                  <th className="py-2 pr-4 text-right">ДРР</th>
                  <th className="py-2 pr-4 text-right">Заказы</th>
                  <th className="py-2 pr-4 text-right">Клики</th>
                  <th className="py-2 pr-4 text-right">Продажи</th>
                  <th className="py-2 pr-4 text-right">Прогноз рекл.</th>
                  <th className="py-2 pr-4 text-right">Прогноз заказов</th>
                  <th className="py-2 pr-4 text-right">Прогноз кликов</th>
                  <th className="py-2 pr-4 text-right">Прогноз продаж</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((item, index) => (
                  <tr key={`${item.displayName}-${index}`} className="hover:bg-slate-50">
                    <td className="py-2 px-2 text-center text-slate-500">{index + 1}</td>
                    <td className="py-2 pr-4 text-left" title={item.displayName}>
                      {item.displayName}
                    </td>
                    <td className="py-2 pr-4 text-right">{fmtNum(item.adsTotal)}</td>
                    <td className={`py-2 pr-4 text-right ${drrTextClass(item.drr)}`}>
                      {formatDrr(item.drr)}
                    </td>
                    <td className="py-2 pr-4 text-right">{fmtNum(item.adsOrders || 0)}</td>
                    <td className="py-2 pr-4 text-right">{fmtNum(item.adsClicks || 0)}</td>
                    <td className="py-2 pr-4 text-right">{fmtNum(item.sales || 0)}</td>
                    <td className="py-2 pr-4 text-right">{fmtNum(item.forecastAdsTotal)}</td>
                    <td className="py-2 pr-4 text-right">{fmtNum(item.forecastOrders)}</td>
                    <td className="py-2 pr-4 text-right">{fmtNum(item.forecastClicks)}</td>
                    <td className="py-2 pr-4 text-right">{fmtNum(item.forecastSales)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="text-sm text-slate-500">Нет данных для отображения.</div>
      )}

      {isRangeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Выбор дат</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600" htmlFor="promo-by-orders-month-modal">
                  Месяц:
                </label>
                <select
                  id="promo-by-orders-month-modal"
                  className="border rounded px-2 py-1 text-sm flex-1"
                  value={selectedMonth}
                  onChange={event => fillRangeFromMonth(event.target.value)}
                >
                  {monthOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600" htmlFor="promo-by-orders-start">
                    С
                  </label>
                  <input
                    id="promo-by-orders-start"
                    type="date"
                    className="border rounded px-2 py-1 text-sm"
                    value={rangeStart}
                    onChange={event => setRangeStart(event.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600" htmlFor="promo-by-orders-end">
                    По
                  </label>
                  <input
                    id="promo-by-orders-end"
                    type="date"
                    className="border rounded px-2 py-1 text-sm"
                    value={rangeEnd}
                    onChange={event => setRangeEnd(event.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="px-3 py-1 rounded border text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setIsRangeModalOpen(false)}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="px-3 py-1 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700"
                  onClick={applyRange}
                >
                  Применить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoByOrdersBlock;
